class MintMainProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mint-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-mint-gallery-slide]')];
    this.thumbnails = [...this.querySelectorAll('[data-mint-gallery-thumb]')];
    this.previousButton = this.querySelector('[data-mint-gallery-prev]');
    this.nextButton = this.querySelector('[data-mint-gallery-next]');
    this.index = Number(this.dataset.mintGalleryInitialIndex || 0);

    this.addEventListener('click', this.onClick);
    this.addEventListener('mint:media-change', this.onMediaChange);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('mint:media-change', this.onMediaChange);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
  }

  onClick = (event) => {
    const thumbnail = event.target.closest('[data-mint-gallery-thumb]');
    if (thumbnail && this.contains(thumbnail)) {
      this.goTo(Number(thumbnail.dataset.mintGalleryIndex));
      return;
    }
    if (event.target.closest('[data-mint-gallery-prev]')) this.goTo(this.index - 1);
    if (event.target.closest('[data-mint-gallery-next]')) this.goTo(this.index + 1);
  };

  onMediaChange = (event) => {
    const mediaId = String(event.detail?.mediaId || '');
    const nextIndex = this.slides.findIndex((slide) => slide.dataset.mintGalleryMediaId === mediaId);
    if (nextIndex >= 0) this.goTo(nextIndex);
  };

  onTouchStart = (event) => { this.touchStartX = event.changedTouches[0]?.clientX; };

  onTouchEnd = (event) => {
    const endX = event.changedTouches[0]?.clientX;
    if (!this.touchStartX || !endX || Math.abs(this.touchStartX - endX) < 45) return;
    this.goTo(this.touchStartX > endX ? this.index + 1 : this.index - 1);
    this.touchStartX = null;
  };

  goTo(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.sync();
  }

  sync() {
    if (this.track) this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
    this.thumbnails.forEach((thumbnail, index) => {
      const active = index === this.index;
      thumbnail.setAttribute('aria-current', String(active));
      if (active) thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
    this.pauseInactiveVideos();
  }

  pauseInactiveVideos() {
    this.slides.forEach((slide, index) => {
      if (index === this.index) return;
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((iframe) => iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
    });
  }
}

class MintMainProductSubscription extends HTMLElement {
  connectedCallback() {
    this.ready = false;
    this.wrapper = this.closest('[data-subscription-block-wrapper]');
    this.planInput = this.querySelector('[data-selling-plan-input]');
    this.frequency = this.querySelector('[data-subscription-frequency]');
    this.subscribeChoice = this.querySelector('[data-subscription-mode="subscribe"]');
    this.oneTimeChoice = this.querySelector('[data-subscription-mode="one-time"]');
    // `title` is a built-in DOMString property on HTMLElement. Assigning the
    // span element to it coerces the node to "[object HTMLSpanElement]" and
    // makes the following `.textContent` update throw.
    this.titleElement = this.querySelector('[data-subscription-title]');
    this.price = this.querySelector('[data-subscription-price]');
    this.comparePrice = this.querySelector('[data-subscription-compare]');
    this.discount = this.querySelector('[data-subscription-discount]');
    this.oneTimePrice = this.querySelector('[data-subscription-one-time-price]');
    this.oneTimeComparePrice = this.querySelector('[data-subscription-one-time-compare]');
    this.modeControls = this.querySelectorAll('[data-subscription-mode-control]');
    this.oneTimeCents = Number(this.dataset.oneTimePrice);
    this.oneTimeCompareCents = Number(this.dataset.oneTimeComparePrice) || this.oneTimeCents;
    this.updateOneTimePrice();
    this.mode = 'subscribe';
    this.plans = this.readPlansFromOptions();

    this.addEventListener('click', this.onClick);
    this.frequency?.addEventListener('change', this.onFrequencyChange);
    this.modeControls.forEach((control) => control.addEventListener('change', this.onModeChange));
    this.setPlan(this.plans[0]);
    this.ready = true;
  }

  disconnectedCallback() {
    this.ready = false;
    this.removeEventListener('click', this.onClick);
    this.frequency?.removeEventListener('change', this.onFrequencyChange);
    this.modeControls.forEach((control) => control.removeEventListener('change', this.onModeChange));
  }

  onClick = (event) => {
    const choice = event.target.closest('[data-subscription-mode]');
    if (!choice || !this.contains(choice)) return;
    this.setMode(choice.dataset.subscriptionMode);
  };

  onFrequencyChange = () => this.setPlan(this.plans.find((plan) => String(plan.id) === this.frequency.value));

  onModeChange = (event) => {
    if (event.target.checked) this.setMode(event.target.value);
  };

  updatePlans(plans, variant) {
    const previousPlanId = String(this.frequency?.value || this.planInput?.value || '');
    const normalizedPlans = Array.isArray(plans)
      ? plans.map((plan) => ({
        id: plan.id,
        name: plan.name,
        price: Number(plan.price),
        compare_at_price: Number(plan.compare_at_price),
      }))
      : [];

    this.oneTimeCents = Number(variant.price);
    this.oneTimeCompareCents = Number(variant.compare_at_price) || this.oneTimeCents;
    this.updateOneTimePrice();

    // Clear the previous plan before changing the variant. Keeping that value in
    // the form lets a stale selling plan be submitted for a different variant.
    this.plans = normalizedPlans;
    if (!normalizedPlans.length) {
      this.wrapper.hidden = true;
      if (this.planInput) {
        this.planInput.value = '';
        this.planInput.disabled = true;
      }
      if (this.frequency) this.frequency.replaceChildren();
      this.dispatchPriceChange(this.oneTimeCents, this.oneTimeCompareCents);
      return false;
    }

    this.wrapper.hidden = false;
    this.frequency.replaceChildren(...normalizedPlans.map((plan) => {
      const option = new Option(plan.name, plan.id);
      option.dataset.planPrice = plan.price;
      option.dataset.planComparePrice = plan.compare_at_price;
      return option;
    }));

    // Delivery cadence can exist in several pack groups. Keep it when that
    // cadence is available for the new variant; otherwise select its first plan.
    const nextPlan = normalizedPlans.find((plan) => String(plan.id) === previousPlanId) || normalizedPlans[0];
    this.setPlan(nextPlan);
    this.setMode(this.mode);
    return true;
  }

  readPlansFromOptions() {
    return [...(this.frequency?.options || [])].map((option) => ({
      id: option.value,
      name: option.textContent,
      price: Number(option.dataset.planPrice),
      compare_at_price: Number(option.dataset.planComparePrice),
    }));
  }

  setPlan(plan) {
    if (!plan) return;
    this.planInput.value = plan.id;
    if (this.frequency) this.frequency.value = plan.id;
    if (this.titleElement) this.titleElement.textContent = 'Subscribe & Save';
    if (this.price) this.price.textContent = this.formatMoney(plan.price);
    if (this.comparePrice) {
      this.comparePrice.textContent = this.formatMoney(plan.compare_at_price);
      this.comparePrice.classList.toggle('is-hidden', !(plan.compare_at_price > plan.price));
    }
    if (this.discount) {
      const percentage = plan.compare_at_price > plan.price
        ? Math.floor(((plan.compare_at_price - plan.price) * 100) / plan.compare_at_price)
        : 0;
      this.discount.textContent = `${percentage}% OFF`;
      this.discount.classList.toggle('is-hidden', percentage === 0);
    }
    if (this.mode === 'subscribe') this.dispatchPriceChange(plan.price, plan.compare_at_price);
  }

  setMode(mode) {
    this.mode = mode === 'one-time' ? 'one-time' : 'subscribe';
    const isSubscription = this.mode === 'subscribe' && this.plans.length > 0;
    if (!isSubscription) this.mode = 'one-time';
    this.subscribeChoice.classList.toggle('is-selected', isSubscription);
    this.oneTimeChoice.classList.toggle('is-selected', !isSubscription);
    this.modeControls.forEach((control) => {
      control.checked = control.value === this.mode;
    });
    this.planInput.disabled = !isSubscription;
    if (isSubscription) {
      this.setPlan(this.plans.find((plan) => String(plan.id) === this.frequency?.value) || this.plans[0]);
    } else {
      this.dispatchPriceChange(this.oneTimeCents, this.oneTimeCompareCents);
    }
  }

  applyCurrentPrice() {
    this.setMode(this.mode);
  }

  updateOneTimePrice() {
    if (this.oneTimePrice) this.oneTimePrice.textContent = this.formatMoney(this.oneTimeCents);
    if (this.oneTimeComparePrice) {
      this.oneTimeComparePrice.textContent = this.formatMoney(this.oneTimeCompareCents);
      this.oneTimeComparePrice.classList.toggle('is-hidden', !(this.oneTimeCompareCents > this.oneTimeCents));
    }
  }

  dispatchPriceChange(price, compareAtPrice) {
    this.dispatchEvent(new CustomEvent('mint-main-product:price-change', {
      bubbles: true,
      detail: {
        price: Number(price),
        compareAtPrice: Number(compareAtPrice),
      },
    }));
  }

  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return (this.dataset.moneyFormat || '${{amount}}')
      .replace(/{{\s*amount(?:_no_decimals)?\s*}}/, amount)
      .replace(/\s*USD\b/, '');
  }
}

class MintMainProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-mint-product-form]');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-product-compare-price]');
    this.discountBadge = this.querySelector('[data-product-discount]');
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.submitLabel = this.querySelector('[data-add-to-cart-label]');
    this.submitPrice = this.querySelector('[data-add-to-cart-price]');
    this.stickyAtc = this.querySelector('[data-mint-sticky-add-to-cart]');
    this.stickyButton = this.querySelector('[data-mint-sticky-add-to-cart-button]');
    this.stickyLabel = this.querySelector('[data-mint-sticky-add-to-cart-label]');
    this.stickyPrice = this.querySelector('[data-mint-sticky-add-to-cart-price]');
    this.stickyImage = this.querySelector('[data-mint-sticky-add-to-cart] .mint-main-product__sticky-image');
    this.quantityInput = this.querySelector('[data-quantity-control] input');
    this.notice = this.querySelector('[data-product-notice]');
    this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    this.sellingPlans = JSON.parse(this.querySelector('[data-variant-selling-plans]')?.textContent || '[]');
    this.subscription = this.querySelector('mint-main-product-subscription');
    this.currentVariant = this.variants.find((variant) => variant.id === Number(this.variantIdInput?.value)) || this.variants[0];
    this.selectedOptions = this.currentVariant ? [...this.currentVariant.options] : [];
    this.purchaseMode = this.querySelector('[data-subscription-mode-control]:checked')?.value || 'subscribe';

    this.addEventListener('click', this.onClick, true);
    this.addEventListener('change', this.onChange, true);
    this.addEventListener('input', this.onInput, true);
    this.addEventListener('mint-main-product:price-change', this.onSubscriptionPriceChange);
    this.form?.addEventListener('submit', this.onSubmit);
    this.updatePrice(this.currentVariant?.price, this.currentVariant?.compare_at_price);
    this.syncSubscriptionPrice();
    if (this.subscription?.ready) {
      this.subscription.applyCurrentPrice();
    }
    this.updateOptionUnitPrices();
    this.initializeStickyAddToCart();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick, true);
    this.removeEventListener('change', this.onChange, true);
    this.removeEventListener('input', this.onInput, true);
    this.removeEventListener('mint-main-product:price-change', this.onSubscriptionPriceChange);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.stickyButton?.removeEventListener('click', this.onStickyAddToCart);
    this.stickyObserver?.disconnect();
  }

  initializeStickyAddToCart() {
    if (!this.stickyAtc || !this.stickyButton || !this.submitButton) return;
    this.stickyButton.addEventListener('click', this.onStickyAddToCart);
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      this.stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
    });
    this.stickyObserver.observe(this.submitButton);
    this.syncStickyAddToCart();
  }

  onStickyAddToCart = () => {
    if (!this.submitButton?.disabled) this.submitButton?.click();
  };

  syncStickyAddToCart() {
    if (!this.stickyButton) return;
    this.stickyButton.disabled = Boolean(this.submitButton?.disabled);
    if (this.stickyLabel && this.submitLabel) this.stickyLabel.textContent = this.submitLabel.textContent;
    if (this.stickyPrice && this.submitPrice) this.stickyPrice.textContent = this.submitPrice.textContent;
    const imageSrc = this.currentVariant?.featured_image?.src || this.currentVariant?.featured_media?.preview_image?.src;
    if (this.stickyImage && imageSrc) {
      this.stickyImage.src = imageSrc;
      this.stickyImage.srcset = imageSrc;
    }
  }

  onClick = (event) => {
    const subscriptionChoice = event.target.closest('[data-subscription-mode]');
    if (subscriptionChoice && this.contains(subscriptionChoice)) {
      this.setPurchaseMode(subscriptionChoice.dataset.subscriptionMode);
      return;
    }

    const option = event.target.closest('button[data-option-value]');
    if (option && this.contains(option)) {
      event.stopPropagation();
      this.selectOption(option);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton && this.contains(quantityButton)) {
      if (!this.quantityInput) return;
      this.quantityInput.value = Math.max(1, this.getQuantity() + Number(quantityButton.dataset.quantityChange));
      this.updateAddToCartPrice();
    }
  };

  onChange = (event) => {
    const modeControl = event.target.closest('[data-subscription-mode-control]');
    if (modeControl && this.contains(modeControl) && modeControl.checked) {
      this.setPurchaseMode(modeControl.value);
      return;
    }

    const frequency = event.target.closest('[data-subscription-frequency]');
    if (frequency && this.contains(frequency)) this.setPurchaseMode('subscribe');

    const quantityInput = event.target.closest('[data-quantity-control] input');
    if (quantityInput && this.contains(quantityInput)) {
      this.normalizeQuantity();
      this.updateAddToCartPrice();
    }
  };

  onInput = (event) => {
    const quantityInput = event.target.closest('[data-quantity-control] input');
    if (quantityInput && this.contains(quantityInput)) this.updateAddToCartPrice();
  };

  selectOption(button) {
    const fieldset = button.closest('[data-option-index]');
    const optionIndex = Number(fieldset.dataset.optionIndex);
    this.selectedOptions[optionIndex] = button.dataset.optionValue;
    fieldset.querySelectorAll('button[data-option-value]').forEach((item) => {
      const selected = item === button;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', String(selected));
    });
    const variant = this.variants.find((item) => item.options.every((value, index) => value === this.selectedOptions[index]));
    if (variant) this.updateVariant(variant);
  }

  onSubscriptionPriceChange = (event) => {
    this.updatePrice(event.detail.price, event.detail.compareAtPrice);
    this.updateOptionUnitPrices();
  };

  syncSubscriptionPrice() {
    if (this.purchaseMode !== 'subscribe') return;
    const selectedPlan = this.subscription?.querySelector('[data-subscription-frequency] option:checked');
    if (selectedPlan) this.updatePrice(selectedPlan.dataset.planPrice, selectedPlan.dataset.planComparePrice);
  }

  setPurchaseMode(mode) {
    this.purchaseMode = mode === 'one-time' ? 'one-time' : 'subscribe';
    this.subscription?.setMode?.(this.purchaseMode);
    // The subscription component can fall back to one-time purchase when the
    // newly selected variant has no eligible selling plan.
    if (this.subscription && typeof this.subscription.mode === 'string' && this.subscription.mode !== this.purchaseMode) {
      this.purchaseMode = this.subscription.mode;
    }

    if (this.purchaseMode === 'one-time') {
      this.updatePrice(this.currentVariant?.price, this.currentVariant?.compare_at_price);
      this.updateOptionUnitPrices();
      return;
    }

    this.syncSubscriptionPrice();
    this.updateOptionUnitPrices();
  }

  updateVariant(variant) {
    this.currentVariant = variant;
    if (this.variantIdInput) this.variantIdInput.value = variant.id;
    this.updatePrice(variant.price, variant.compare_at_price);
    if (this.submitButton) this.submitButton.disabled = !variant.available;
    if (this.submitLabel) this.submitLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
    this.syncStickyAddToCart();

    // Read the selected control at the moment the variant changes. The value in
    // `purchaseMode` can be one event behind while a label's native radio
    // behaviour is still being processed.
    const selectedMode = this.subscription?.mode
      || this.querySelector('[data-subscription-mode-control]:checked')?.value
      || this.purchaseMode;
    this.purchaseMode = selectedMode;

    const sellingPlans = this.sellingPlans.find((item) => Number(item.id) === Number(variant.id));
    const plans = sellingPlans?.plans || [];
    const subscription = this.subscription || this.querySelector('mint-main-product-subscription');
    let hasSubscription;

    // Sections can be injected by the theme editor after this file has already
    // run. In that case the element exists in the DOM but may not be upgraded
    // yet; defer the sync until its custom-element definition is ready.
    if (subscription && (typeof subscription.updatePlans !== 'function' || !subscription.ready)) {
      const syncWhenReady = () => {
        if (this.currentVariant?.id !== variant.id || !this.isConnected) return;
        this.subscription = this.querySelector('mint-main-product-subscription');
        if (!this.subscription || typeof this.subscription.updatePlans !== 'function' || !this.subscription.ready) {
          requestAnimationFrame(syncWhenReady);
          return;
        }
        const result = this.subscription.updatePlans(plans, variant);
        if (result === false) this.purchaseMode = 'one-time';
        this.setPurchaseMode(this.purchaseMode);
      };
      customElements.whenDefined('mint-main-product-subscription').then(syncWhenReady);
    } else {
      this.subscription = subscription;
      hasSubscription = subscription?.updatePlans?.(plans, variant);
    }
    if (hasSubscription === false) this.purchaseMode = 'one-time';
    this.setPurchaseMode(this.purchaseMode);
    if (variant.featured_media?.id) this.querySelector('mint-main-product-gallery')?.dispatchEvent(new CustomEvent('mint:media-change', { bubbles: true, detail: { mediaId: variant.featured_media.id } }));

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updateOptionUnitPrices() {
    const activePlanName = this.subscription?.querySelector('[data-subscription-frequency] option:checked')?.textContent?.trim();
    const useSellingPlan = this.purchaseMode === 'subscribe' && Boolean(activePlanName);

    this.querySelectorAll('[data-mint-option-unit-price]').forEach((unitPrice) => {
      const variantId = Number(unitPrice.dataset.mintOptionVariantId);
      const mintQuantity = Number(unitPrice.dataset.mintOptionQuantity);
      const variant = this.variants.find((item) => Number(item.id) === variantId);
      if (!variant || !mintQuantity) return;

      let price = Number(variant.price);
      if (useSellingPlan) {
        const plans = this.sellingPlans.find((item) => Number(item.id) === variantId)?.plans || [];
        const matchingPlan = plans.find((plan) => plan.name === activePlanName);
        if (matchingPlan) price = Number(matchingPlan.price);
      }

      unitPrice.textContent = `${this.formatMoney(price / mintQuantity)} / mint`;
    });
  }

  updatePrice(price, compareAtPrice) {
    const selectedPrice = Number(price);
    const selectedCompareAtPrice = Number(compareAtPrice);
    this.selectedPrice = selectedPrice;
    if (this.price) this.price.textContent = this.formatMoney(selectedPrice);
    if (this.comparePrice) {
      this.comparePrice.textContent = this.formatMoney(selectedCompareAtPrice);
      this.comparePrice.classList.toggle('is-hidden', !(selectedCompareAtPrice > selectedPrice));
    }
    if (this.discountBadge) {
      const discount = selectedCompareAtPrice > selectedPrice
        ? Math.floor(((selectedCompareAtPrice - selectedPrice) * 100) / selectedCompareAtPrice)
        : 0;
      this.discountBadge.textContent = `${discount}% OFF`;
      this.discountBadge.classList.toggle('is-hidden', discount === 0);
    }
    this.updateAddToCartPrice();
  }

  getQuantity() {
    return Math.max(1, Math.floor(Number(this.quantityInput?.value || 1)) || 1);
  }

  normalizeQuantity() {
    if (this.quantityInput) this.quantityInput.value = this.getQuantity();
  }

  updateAddToCartPrice() {
    if (this.submitPrice) this.submitPrice.textContent = this.formatMoney(Number(this.selectedPrice || 0) * this.getQuantity());
    this.syncStickyAddToCart();
  }

  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return (this.dataset.moneyFormat || '${{amount}}')
      .replace(/{{\s*amount(?:_no_decimals)?\s*}}/, amount)
      .replace(/\s*USD\b/, '');
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (!this.form || this.submitButton?.disabled) return;
    this.submitButton.disabled = true;
    this.submitLabel.textContent = 'Adding…';
    this.syncStickyAddToCart();
    this.setNotice('');

    try {
      const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form) });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || 'Unable to add this item to cart.');
      this.dispatchEvent(new CustomEvent('mint-main-product:added', { bubbles: true, detail: { item } }));
      const cartDrawer = document.querySelector('cart-form#ajax-cart');
      if (typeof cartDrawer?.getCartData === 'function') {
        cartDrawer.getCartData('open_drawer', { skipShippingProtectionSync: true });
      } else {
        window.location.assign(this.dataset.cartUrl);
      }
    } catch (error) {
      this.setNotice(error.message || 'Unable to add this item to cart.');
    } finally {
      const isAvailable = Boolean(this.currentVariant?.available);
      this.submitButton.disabled = !isAvailable;
      this.submitLabel.textContent = isAvailable ? 'Add to cart' : 'Sold out';
      this.updateAddToCartPrice();
      this.syncStickyAddToCart();
    }
  };

  setNotice(message) {
    if (!this.notice) return;
    this.notice.hidden = !message;
    this.notice.textContent = message;
  }
}

class MintComplementaryProducts extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onSubmit);
  }

  onClick = (event) => {
    const button = event.target.closest('[data-mint-complementary-prev], [data-mint-complementary-next]');
    if (!button || !this.contains(button)) return;

    const track = this.querySelector('[data-mint-complementary-track]');
    const card = track?.querySelector('.mint-main-product__complementary-card');
    if (!track) return;

    const direction = button.hasAttribute('data-mint-complementary-next') ? 1 : -1;
    const amount = (card?.getBoundingClientRect().width || track.clientWidth) + 8;
    track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  };

  onSubmit = async (event) => {
    const form = event.target.closest('.mint-main-product__complementary-form');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button?.disabled) return;
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || 'Unable to add this item to cart.');

      const cartDrawer = document.querySelector('cart-form#ajax-cart');
      if (typeof cartDrawer?.getCartData === 'function') {
        cartDrawer.getCartData('open_drawer', { skipShippingProtectionSync: true });
      } else {
        window.location.assign('/cart');
      }
      button.disabled = false;
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  };
}

if (!customElements.get('mint-main-product-gallery')) customElements.define('mint-main-product-gallery', MintMainProductGallery);
if (!customElements.get('mint-main-product-subscription')) customElements.define('mint-main-product-subscription', MintMainProductSubscription);
if (!customElements.get('mint-main-product')) customElements.define('mint-main-product', MintMainProduct);
if (!customElements.get('mint-complementary-products')) customElements.define('mint-complementary-products', MintComplementaryProducts);
